import {Component, computed, DestroyRef, inject, Inject, OnInit, Signal, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {errorText} from '../../../graphql/error-link';
import {LoadRowComponent} from '../../../graphql/load-row.component';
import {Apollo, QueryRef} from 'apollo-angular';
import gql from 'graphql-tag';
import {ProcessStateEnum, Query as WorkflowQuery} from '../../../types-workflow';
import {File as RepoFile, Folder, Mutation, Query as RepoQuery} from '../../../types-repository';
import {firstValueFrom, forkJoin, Observable, of, Subscription} from 'rxjs';
import {catchError, filter, map, skipWhile, switchMap} from 'rxjs/operators';
import {BannerService} from '../../../banner/banner.service';
import {DIALOG_DATA, DialogRef} from '@angular/cdk/dialog';
import { AsyncPipe, CommonModule } from '@angular/common';
import {FileSizePipe} from '../../../pipes/file-size-pipe';
import {DateComponent} from '../../../date/date.component';
import {ContextService} from '../../../services/context.service';
import {FormsModule} from '@angular/forms';
import {ProcessService} from '../../../services/process.service';
import {SelectionModel} from '@angular/cdk/collections';
import { downloadZip } from 'client-zip'

const ENCODE_LOCATION_QUERY = gql`
  query RetrieveEncodeLocation($po_name: String!, $po_item_id: String) {
    list_POs(input: { filter: { po_name: { eq: $po_name}}} ){
      pos {
        poitems(po_item_id: $po_item_id) {
          id
          po_item_id
          encode_location
        }
      }
    }
  }
`;

const FOLDER_QUERY = gql`
  query FolderFiles($id: ID!) {
    folder(id: $id) {
      files {
        id
        name
        size
        last_modified
      }
      folders {
        id
        name
        files {
          id
          name
          size
          last_modified
        }
      }
    }
  }
`;

@Component({
  selector: 'app-manage-files-dialog',
  templateUrl: './manage-files-dialog.component.html',
  styleUrls: ['./manage-files-dialog.component.scss'],
  imports: [
    LoadRowComponent,
    AsyncPipe,
    FileSizePipe,
    CommonModule,
    DateComponent,
    FormsModule,
  ]
})
export class ManageFilesDialogComponent implements OnInit {

  encodes$: Observable<RepoFile[]>
  /** The listed encodes; null until the first load has finished (or failed). */
  encodesList = signal<RepoFile[] | null>(null);
  /** Why the encodes (or some of their folders) could not be listed. */
  loadError = signal<string | null>(null);
  errorState = computed(() => ({loading: false, data: null, error: this.loadError()}));
  private destroyRef = inject(DestroyRef);
  selection = new SelectionModel<RepoFile>(true, [], true, (a, b) => a.id === b.id);

  // Track editing state per encode
  editingId: string | null = null;
  editingName: string = '';
  renameInflight = signal<string | null>(null);
  deleteInflight = signal<string | null>(null);
  deleteConfirmId: string | null = null;
  zipInflight = signal(false);
  zipProgress = signal<{ done: number; total: number } | null>(null);
  zipFailures = signal<Map<string, string>>(new Map());

  constructor(public dialogRef: DialogRef<ManageFilesDialogComponent>,
              private apollo: Apollo,
              private banner: BannerService,
              public contextService: ContextService,
              private processService: ProcessService,
              @Inject(DIALOG_DATA) public data: { header: string, po_name: string, po_item_id?: string }) {
  }

  private encodesSubscription?: Subscription;

  ngOnInit(): void {
    this.encodes$ = this.apollo.use('workflow').watchQuery<WorkflowQuery>({
      query: ENCODE_LOCATION_QUERY,
      fetchPolicy: 'cache-first',
      variables: {
        'po_name': this.data.po_name,
        'po_item_id': this.data.po_item_id
      }
    }).valueChanges.pipe(
      // A failed load arrives as a result with an error, or is thrown; both end in the error row
      // instead of "Loading ..." or "No encodes available".
      catchError(error => of({data: undefined, error} as any)),
      filter(d => {
        if (!d.data?.list_POs?.pos && d.error) {
          this.loadError.set(errorText(d.error));
          this.encodesList.set([]);
        }
        return !!d.data?.list_POs?.pos;
      }),
      map(d =>
        d.data.list_POs.pos
          .flatMap(po => po.poitems)
          .map(item => item.encode_location)
          .filter((loc): loc is string => !!loc)
      ),
      switchMap(locations => {
        this.loadError.set(null);
        if (locations.length === 0) return of([]);
        return forkJoin(locations.map(location =>
          this.apollo.query<RepoQuery>({
            query: FOLDER_QUERY,
            variables: { id: location },
            fetchPolicy: 'network-only',
          }).pipe(
            map(res => {
              const folder = res.data.folder;
              const files: RepoFile[] = [...folder.files];
              for (const subfolder of folder.folders) {
                for (const file of subfolder.files) {
                  files.push({...file, name: subfolder.name + '/' + file.name});
                }
              }
              return files;
            }),
            // One unreadable folder keeps the others listed, and says so.
            catchError(error => {
              this.loadError.set(errorText(error));
              return of([] as RepoFile[]);
            })
          )
        )).pipe(map((results: RepoFile[][]) => results.flat()));
      }),
      map(d => d.slice().sort((a, b) => a.name.localeCompare(b.name))),
    )
    // ngOnInit runs again after a rename or delete: end the previous load rather than keep both watching.
    this.encodesSubscription?.unsubscribe();
    this.encodesSubscription = this.encodes$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(e => this.encodesList.set(e));
  }

  private generateDownloadUrl(encode: RepoFile): Promise<string> {
    const query = gql`mutation GenerateDownloadUrl($encode_id: ID!) {
      generateDownloadUrl(input: {file_id: $encode_id}) {
        signed_url
      }
    }
    `
    return firstValueFrom(this.apollo.mutate<Mutation>({
      mutation: query,
      variables: {
        encode_id: encode.id
      }
    })).then(d => {
      if (!d.data) {
        throw new Error('No data returned from generateDownloadUrl');
      }
      return d.data.generateDownloadUrl.signed_url;
    });
  }

  download(encode: RepoFile, onError?: (err: Error) => void) {
    this.zipFailures.update(m => {
      if (!m.has(encode.id)) return m;
      const next = new Map(m);
      next.delete(encode.id);
      return next;
    });
    this.generateDownloadUrl(encode).then(signed_url => {
      const a = document.createElement('a');
      a.href = signed_url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }, (err: Error) => {
      if (onError) {
        onError(err);
      } else {
        this.dialogRef.close();
        this.banner.open('Download failed: ' + err.message, ['dismiss']);
      }
    });
  }

  private fetchWithTimeout(url: string, timeoutMs = 20000): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return fetch(url, { signal: controller.signal }).finally(() => clearTimeout(timer));
  }

  private async fetchFile(f: RepoFile): Promise<{ name: string; input: Response }> {
    const signed_url = await this.generateDownloadUrl(f);
    const response = await this.fetchWithTimeout(signed_url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return { name: f.name, input: response };
  }

  async downloadSelectedAsZip(files: RepoFile[], concurrency = 4) {
    this.zipInflight.set(true);
    this.zipProgress.set({ done: 0, total: files.length });
    this.zipFailures.update(m => {
      const next = new Map(m);
      for (const f of files) next.delete(f.id);
      return next;
    });
    try {
      const inputs: { name: string; input: Response }[] = [];
      const failures = new Map<string, string>();
      let nextIndex = 0;
      let doneCount = 0;

      const worker = async () => {
        while (nextIndex < files.length) {
          const i = nextIndex++;
          const file = files[i];
          try {
            inputs.push(await this.fetchFile(file));
          } catch (err) {
            const reason = err instanceof DOMException && err.name === 'AbortError'
              ? 'timed out'
              : (err as Error).message;
            failures.set(file.id, reason);
          }
          doneCount++;
          this.zipProgress.set({ done: doneCount, total: files.length });
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(concurrency, files.length) }, () => worker())
      );

      if (inputs.length === 0) {
        throw new Error('All files failed to download');
      }

      const zipResponse = downloadZip(inputs);
      const blob = await zipResponse.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.data.po_name}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      if (failures.size > 0) {
        this.zipFailures.update(m => new Map([...m, ...failures]));
      }
    } catch (err) {
      console.error('downloadSelectedAsZip failed', err);
      const message = err instanceof DOMException && err.name === 'AbortError'
        ? 'Download failed: request timed out'
        : 'Download failed: ' + (err as Error).message;
      this.banner.open(message, ['dismiss']);
    } finally {
      this.zipInflight.set(false);
      this.zipProgress.set(null);
    }
  }

  canDownload(encode: RepoFile): boolean {
    return this.contextService.isAllowed(
      'contentplatform:repository:Mutation/GenerateDownloadUrl',
      `contentplatform:folder:input.file_id/${encode.id}`
    )();
  }

  toggleAll(encodes: RepoFile[]) {
    const selectable = encodes.filter(f => this.canDownload(f));
    const allSelected = selectable.length > 0 && selectable.every(e => this.selection.isSelected(e));
    
    if (allSelected) {
      this.selection.deselect(...selectable);
    } else {
      this.selection.select(...selectable);
    }
  }

  isAllSelected(encodes: RepoFile[]) {
    const selectable = encodes.filter(f => this.canDownload(f));
    return this.selection.selected.length === selectable.length;
  }

  isIndeterminate(encodes: RepoFile[]) {
    const selectable = encodes.filter(f => this.canDownload(f));
    return this.selection.selected.length > 0 && this.selection.selected.length < selectable.length;
  }

  confirmDelete(encode: RepoFile) {
    this.deleteConfirmId = encode.id;
  }

  cancelDelete() {
    this.deleteConfirmId = null;
  }

  deleteEncode(encode: RepoFile) {
    this.deleteConfirmId = null;
    this.deleteInflight.set(encode.id);

    const query = gql`mutation DeleteEncode($encode_id: ID!) {
      delete(input: {id: $encode_id}) {
        file {
          id
        }
      }
    }
    `
    this.apollo.mutate<Mutation>({
      mutation: query,
      variables: {
        encode_id: encode.id
      }
    }).subscribe({
      next: () => {
        this.deleteInflight.set(null);
        // Re-trigger by refetching - the switchMap will re-query the repository
        this.ngOnInit();
      },
      error: (err) => {
        this.deleteInflight.set(null);
        this.banner.open('Delete failed: ' + err.message, ['dismiss']);
      }
    });
  }

  startRename(encode: RepoFile) {
    this.editingId = encode.id;
    this.editingName = encode.name;
  }

  cancelRename() {
    this.editingId = null;
    this.editingName = '';
  }

  saveRename(encode: RepoFile) {
    if (this.editingName === encode.name || !this.editingName.trim()) {
      this.cancelRename();
      return;
    }

    // Get the folder path from the encode id (everything except the filename)
    const folderPath = encode.id.substring(0, encode.id.lastIndexOf('/') + 1);
    const newPath = folderPath + this.editingName;

    this.renameInflight.set(encode.id);
    this.editingId = null;
    this.editingName = '';

    const mutation = gql`mutation RenameEncode($src: String!, $dest: String!) {
      move_file(input: {src: $src, dest: $dest}) {
        __typename
        id
        state
        message
        start_date
        end_date
      }
    }`;

    this.apollo.mutate<Mutation>({
      mutation: mutation,
      variables: {
        src: encode.id,
        dest: newPath
      }
    }).pipe(
      switchMap(res => this.processService.observeProcess(res.data.move_file)),
      skipWhile(process => process.state === ProcessStateEnum.InProgress)
    ).subscribe({
      next: (process) => {
        if (process.state === ProcessStateEnum.Success) {
          this.renameInflight.set(null);
          this.ngOnInit();
        } else {
          this.renameInflight.set(null);
          this.banner.open('Rename failed: ' + process.message, ['dismiss']);
        }
      },
      error: (err) => {
        this.renameInflight.set(null);
        this.banner.open('Rename failed: ' + err.message, ['dismiss']);
      }
    });
  }

  canRename(encode: RepoFile): boolean {
    // Get the folder path from the encode id
    const folderPath = encode.id.substring(0, encode.id.lastIndexOf('/') + 1);
    return this.contextService.isAllowed(
      'contentplatform:repository:Mutation/MoveFile',
      `contentplatform:file:input.src/${encode.id}`
    )();
  }
}
